import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import {
  AGE_OPTIONS,
  HARDEST_OPTIONS,
  CONTEXT_BRANCHES,
  branchFor,
  ackForHardest,
  EMOTIONAL_OPTIONS,
  pickEmotionalResponse,
  INTENT_OPTIONS,
  INTENT_ACK,
  buildSummaryParts,
} from "../flow/conversationData";
import { trackEvent } from "../lib/tracking";
import { CALENDLY_URL } from "../lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUMMARY_IMG =
  "https://images.unsplash.com/photo-1675371303507-90986d992337?q=80&w=1000&auto=format&fit=crop";

const Choice = ({ selected, onClick, testid, children }) => (
  <button
    type="button"
    aria-pressed={selected}
    data-testid={testid}
    onClick={onClick}
    className={`w-full flex items-center justify-between gap-4 px-6 py-5 rounded-2xl border text-left min-h-[60px] transition-all duration-300 ${
      selected
        ? "bg-euca border-euca text-white shadow-lg shadow-euca/25"
        : "bg-white/70 border-ink/10 text-ink hover:border-clay/60 hover:shadow-md hover:-translate-y-0.5"
    }`}
  >
    <span className="text-base font-medium">{children}</span>
    <span
      className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center transition-all duration-300 ${
        selected ? "bg-white/25" : "border border-ink/15"
      }`}
    >
      {selected && <Check size={14} />}
    </span>
  </button>
);

const ClayCTA = ({ children, onClick, testid, disabled, type = "button" }) => (
  <button
    type={type}
    disabled={disabled}
    data-testid={testid}
    onClick={onClick}
    className="inline-flex items-center gap-3 rounded-full bg-clay text-white font-medium px-8 py-4 text-base hover:bg-claydark transition-colors duration-300 shadow-lg shadow-clay/25 disabled:opacity-40 disabled:shadow-none"
  >
    {children}
    <ArrowRight size={17} />
  </button>
);

const Question = ({ children, hint, label }) => (
  <div>
    {label && (
      <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca mb-5">{label}</p>
    )}
    <h2 className="font-serif font-light text-3xl sm:text-[2.6rem] leading-[1.12] tracking-tight text-ink">
      {children}
    </h2>
    {hint && <p className="mt-4 text-sm font-normal text-smoke">{hint}</p>}
  </div>
);

const AckScreen = ({ ack, testid, onContinue }) => (
  <button data-testid={testid} onClick={onContinue} className="w-full text-left">
    <div className="max-w-xl">
      <p className={`font-serif font-light leading-[1.12] tracking-tight text-ink ${ack?.big ? "text-4xl sm:text-5xl" : "text-3xl sm:text-4xl"}`}>
        {ack?.title}
      </p>
      <p className="mt-6 text-base sm:text-lg font-normal leading-relaxed text-smoke">
        {ack?.body}
      </p>
      <span className="mt-12 inline-flex items-center gap-3 text-sm font-medium text-clay">
        Continue <ArrowRight size={16} />
      </span>
    </div>
  </button>
);

const THEME_FOR = (current, emotionalAck) => {
  if (current === "ack-hardest" || current === "success") return "bg-eucasoft";
  if (current === "ack-intent" || current === "ack-emotional") {
    if (emotionalAck?.id === "judged" || emotionalAck?.id === "wrong-thing") return "bg-blushfield";
    if (emotionalAck?.id === "conflicting") return "bg-claysoft";
    if (emotionalAck?.id === "bad-habits") return "bg-mineralsoft";
    return "bg-claysoft";
  }
  if (current === "summary") return "bg-quiet";
  if (current === "andrea") return "bg-blushfield";
  return "bg-ivory";
};

export const Conversation = ({ open, onClose, prefillAge, onAgeSelected }) => {
  const reduce = useReducedMotion();
  const [stepIdx, setStepIdx] = useState(0);
  const [intro, setIntro] = useState(false);
  const [answers, setAnswers] = useState({ age: null, hardest: [], context: [], emotional: [], intent: null, tried: "" });
  const [contact, setContact] = useState({ first_name: "", email: "", phone: "", preferred_contact: "Email" });
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);

  const steps = useMemo(() => {
    const s = ["age", "hardest"];
    if (ackForHardest(answers.hardest)) s.push("ack-hardest");
    s.push("context", "emotional");
    if (pickEmotionalResponse(answers.emotional.map((e) => e.id))) s.push("ack-emotional");
    s.push("intent");
    if (answers.intent === "tried-everything") s.push("ack-intent", "tried");
    s.push("summary", "andrea", "contact");
    return s;
  }, [answers.hardest, answers.emotional, answers.intent]);

  const current = done ? "success" : steps[Math.min(stepIdx, steps.length - 1)];
  const emotionalAck = pickEmotionalResponse(answers.emotional.map((e) => e.id));
  const theme = intro ? "bg-ivory" : THEME_FOR(current, emotionalAck);

  useEffect(() => {
    if (stepIdx > steps.length - 1) setStepIdx(steps.length - 1);
  }, [steps.length, stepIdx]);

  useEffect(() => {
    if (!open) return;
    if (prefillAge) {
      setAnswers((a) => ({ ...a, age: prefillAge }));
      trackEvent("age_selected", { age: prefillAge, source: "hero" });
      onAgeSelected?.(prefillAge);
      setStepIdx(1);
      setIntro(true);
      const t = setTimeout(() => setIntro(false), 1900);
      return () => clearTimeout(t);
    }
  }, [open, prefillAge]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    trackEvent("experience_started");
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [current]);

  useEffect(() => {
    if (!open || intro) return;
    if (current === "summary") trackEvent("summary_viewed");
    if (current === "contact") trackEvent("contact_step_viewed");
  }, [current, open, intro]);

  useEffect(() => {
    if (!open || intro || !current.startsWith("ack")) return;
    const t = setTimeout(() => next(), 4200);
    return () => clearTimeout(t);
  }, [current, open, intro]);

  const next = () => setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const reset = () => {
    setAnswers({ age: null, hardest: [], context: [], emotional: [], intent: null, tried: "" });
    setContact({ first_name: "", email: "", phone: "", preferred_contact: "Email" });
    setStepIdx(0);
    setIntro(false);
    setDone(false);
    setFailed(false);
  };

  const handleClose = () => {
    onClose();
    if (done) reset();
  };

  const toggleIn = (key, item) =>
    setAnswers((a) => ({
      ...a,
      [key]: a[key].some((x) => x.id === item.id)
        ? a[key].filter((x) => x.id !== item.id)
        : [...a[key], item],
    }));

  const selectAge = (o) => {
    setAnswers((a) => ({ ...a, age: o.id }));
    trackEvent("age_selected", { age: o.id });
    onAgeSelected?.(o.id);
    setTimeout(next, reduce ? 0 : 350);
  };

  const selectIntent = (o) => {
    setAnswers((a) => ({ ...a, intent: o.id }));
    trackEvent("intent_selected", { intent: o.id });
    setTimeout(next, reduce ? 0 : 350);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setFailed(false);
    try {
      await axios.post(`${API}/leads`, {
        parent_name: contact.first_name,
        email: contact.email,
        phone: contact.phone || null,
        preferred_contact: contact.preferred_contact,
        child_age: AGE_OPTIONS.find((o) => o.id === answers.age)?.label || "",
        hardest: answers.hardest.map((h) => h.label),
        context: answers.context.map((c) => c.label),
        emotional: answers.emotional.map((x) => x.label),
        intent: INTENT_OPTIONS.find((o) => o.id === answers.intent)?.label || "",
        tried: answers.tried || null,
      });
      trackEvent("lead_submitted");
      setDone(true);
    } catch (err) {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  const progress = done ? 100 : (stepIdx / (steps.length - 1)) * 100;
  const hardestAck = ackForHardest(answers.hardest);
  const branch = CONTEXT_BRANCHES[branchFor(answers.hardest[0]?.id)];
  const summaryParts = buildSummaryParts(answers);
  const firstHardest = answers.hardest[0]?.label || "";
  const ageLabel = AGE_OPTIONS.find((o) => o.id === answers.age)?.label || "";

  const inputCls =
    "w-full bg-white border border-ink/10 focus:border-clay outline-none rounded-xl px-4 py-3.5 text-base font-normal text-ink placeholder:text-dusk transition-colors duration-300";

  const stepMotion = {
    initial: reduce ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    exit: reduce ? {} : { opacity: 0, y: -14 },
    transition: { duration: 0.4, ease: "easeOut" },
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          role="dialog"
          aria-modal="true"
          aria-label="Start with your child"
          data-testid="conversation-overlay"
          data-lenis-prevent
          className={`fixed inset-0 z-[80] flex flex-col h-screen h-[100dvh] text-ink transition-colors duration-700 ${theme}`}
        >
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-ink/10">
            <div
              className="h-full bg-clay transition-[width] duration-500 ease-out"
              style={{ width: `${progress}%` }}
              data-testid="conversation-progress"
            />
          </div>

          <div className="relative flex items-center justify-between px-6 md:px-12 pt-6">
            {stepIdx > 0 && !done && !intro ? (
              <button
                data-testid="conv-back"
                onClick={back}
                className="flex items-center gap-2 text-sm font-normal opacity-70 hover:opacity-100 transition-opacity duration-300 py-2"
              >
                <ArrowLeft size={16} /> Back
              </button>
            ) : (
              <span className="w-16" />
            )}
            <span className="font-serif font-light text-lg opacity-80 select-none" aria-hidden="true">
              The Nurtured Parent Co.
            </span>
            <button
              data-testid="conv-close"
              onClick={handleClose}
              aria-label="Close"
              className="p-2 opacity-70 hover:opacity-100 transition-opacity duration-300"
            >
              <X size={22} />
            </button>
          </div>

          <div ref={scrollRef} className="relative flex-1 overflow-y-auto overscroll-contain">
            <div className="min-h-full flex flex-col px-6 md:px-12 py-14">
              <AnimatePresence mode="wait">
                <motion.div key={intro ? "intro" : current} {...stepMotion} className="w-full max-w-2xl mx-auto my-auto">

                  {intro && (
                    <div className="text-center" data-testid="conv-intro">
                      {ageLabel && (
                        <span className="inline-block bg-eucasoft text-euca rounded-full px-4 py-2 text-xs font-semibold tracking-wide">
                          {ageLabel}
                        </span>
                      )}
                      <p className="mt-8 font-serif font-light text-3xl sm:text-4xl leading-snug text-ink">
                        Okay — let&rsquo;s start there.
                      </p>
                    </div>
                  )}

                  {!intro && current === "age" && (
                    <>
                      <Question label="Let's start here">How old is your little one?</Question>
                      <div className="mt-9 space-y-3">
                        {AGE_OPTIONS.map((o) => (
                          <Choice
                            key={o.id}
                            selected={answers.age === o.id}
                            onClick={() => selectAge(o)}
                            testid={`conv-age-${o.id}`}
                          >
                            {o.label}
                          </Choice>
                        ))}
                      </div>
                    </>
                  )}

                  {!intro && current === "hardest" && (
                    <>
                      <Question hint="Choose any that fit.">What&rsquo;s been hardest lately?</Question>
                      <div className="mt-9 space-y-3">
                        {HARDEST_OPTIONS.map((o) => (
                          <Choice
                            key={o.id}
                            selected={answers.hardest.some((h) => h.id === o.id)}
                            onClick={() => toggleIn("hardest", { id: o.id, label: o.label })}
                            testid={`conv-hardest-${o.id}`}
                          >
                            {o.label}
                          </Choice>
                        ))}
                      </div>
                      <div className="mt-9">
                        <ClayCTA
                          testid="conv-hardest-continue"
                          disabled={!answers.hardest.length}
                          onClick={() => {
                            trackEvent("sleep_problem_selected", { value: answers.hardest.map((h) => h.id) });
                            next();
                          }}
                        >
                          Continue
                        </ClayCTA>
                      </div>
                    </>
                  )}

                  {!intro && current === "ack-hardest" && (
                    <AckScreen ack={hardestAck} testid="conv-ack-hardest" onContinue={next} />
                  )}

                  {!intro && current === "ack-emotional" && (
                    <AckScreen ack={emotionalAck} testid="conv-ack-emotional" onContinue={next} />
                  )}

                  {!intro && current === "ack-intent" && (
                    <AckScreen ack={INTENT_ACK} testid="conv-ack-intent" onContinue={next} />
                  )}

                  {!intro && current === "context" && branch && (
                    <>
                      {firstHardest && (
                        <p className="mb-6 text-sm font-normal text-smoke">
                          You said:{" "}
                          <span className="inline-block bg-eucasoft text-euca rounded-full px-3.5 py-1.5 text-xs font-semibold">
                            {firstHardest}
                          </span>
                        </p>
                      )}
                      <Question hint="Choose any that fit.">{branch.question}</Question>
                      <div className="mt-9 space-y-3">
                        {branch.options.map((o) => (
                          <Choice
                            key={o.id}
                            selected={answers.context.some((c) => c.id === o.id)}
                            onClick={() => toggleIn("context", { id: o.id, label: o.label, fragment: o.fragment })}
                            testid={`conv-context-${o.id}`}
                          >
                            {o.label}
                          </Choice>
                        ))}
                      </div>
                      <div className="mt-9">
                        <ClayCTA
                          testid="conv-context-continue"
                          disabled={!answers.context.length}
                          onClick={() => {
                            trackEvent("context_selected", { value: answers.context.map((c) => c.id) });
                            next();
                          }}
                        >
                          Continue
                        </ClayCTA>
                      </div>
                    </>
                  )}

                  {!intro && current === "emotional" && (
                    <>
                      <Question hint="Choose any that fit. There's no wrong answer.">
                        What&rsquo;s making this feel hardest?
                      </Question>
                      <div className="mt-9 space-y-3">
                        {EMOTIONAL_OPTIONS.map((o) => (
                          <Choice
                            key={o.id}
                            selected={answers.emotional.some((x) => x.id === o.id)}
                            onClick={() => toggleIn("emotional", { id: o.id, label: o.label })}
                            testid={`conv-emotional-${o.id}`}
                          >
                            {o.label}
                          </Choice>
                        ))}
                      </div>
                      <div className="mt-9">
                        <ClayCTA
                          testid="conv-emotional-continue"
                          disabled={!answers.emotional.length}
                          onClick={() => {
                            trackEvent("emotional_concern_selected", { value: answers.emotional.map((x) => x.id) });
                            next();
                          }}
                        >
                          Continue
                        </ClayCTA>
                      </div>
                    </>
                  )}

                  {!intro && current === "intent" && (
                    <>
                      <Question>Where are you with this right now?</Question>
                      <div className="mt-9 space-y-3">
                        {INTENT_OPTIONS.map((o) => (
                          <Choice
                            key={o.id}
                            selected={answers.intent === o.id}
                            onClick={() => selectIntent(o)}
                            testid={`conv-intent-${o.id}`}
                          >
                            {o.label}
                          </Choice>
                        ))}
                      </div>
                    </>
                  )}

                  {!intro && current === "tried" && (
                    <>
                      <Question hint="Optional — a sentence is plenty.">What have you tried so far?</Question>
                      <textarea
                        data-testid="conv-tried-input"
                        rows={3}
                        value={answers.tried}
                        onChange={(e) => setAnswers((a) => ({ ...a, tried: e.target.value }))}
                        placeholder="Routines, schedules, advice from friends — anything."
                        className={`${inputCls} mt-9 resize-none`}
                      />
                      <div className="mt-9 flex items-center gap-8">
                        <ClayCTA testid="conv-tried-continue" onClick={next}>
                          Continue
                        </ClayCTA>
                        <button
                          data-testid="conv-tried-skip"
                          onClick={next}
                          className="text-sm underline underline-offset-8 decoration-ink/25 hover:decoration-ink transition-[text-decoration-color] duration-300"
                        >
                          Skip
                        </button>
                      </div>
                    </>
                  )}

                  {!intro && current === "summary" && (
                    <div data-testid="conv-summary" className="max-w-5xl grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                      <div className="lg:col-span-7">
                        <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
                          Here&rsquo;s what I&rsquo;m hearing
                        </p>
                        <div className="mt-10 space-y-9">
                          {summaryParts.map((p, i) => (
                            <motion.div
                              key={i}
                              initial={reduce ? false : { opacity: 0, y: 14 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.6, delay: 0.2 + i * 0.16 }}
                              className={i % 2 === 1 ? "sm:ml-12" : ""}
                            >
                              {p.pre && (
                                <p className="text-sm font-normal text-smoke">{p.pre}</p>
                              )}
                              <p className="font-serif font-light text-2xl sm:text-4xl leading-[1.15] tracking-tight text-ink">
                                {p.em}
                              </p>
                              {p.post && (
                                <p className="mt-1 text-sm font-normal text-smoke">{p.post}</p>
                              )}
                            </motion.div>
                          ))}
                        </div>
                        <p className="mt-12 text-lg sm:text-xl font-normal leading-relaxed text-smoke max-w-lg">
                          You don&rsquo;t need another generic list of sleep
                          tips. You need someone to look at{" "}
                          <em className="font-serif text-ink">your</em> situation.
                        </p>
                        <p className="mt-6 font-serif font-light text-3xl sm:text-4xl leading-snug text-ink">
                          That&rsquo;s where Andrea comes in.
                        </p>
                        <div className="mt-10">
                          <ClayCTA testid="conv-summary-cta" onClick={next}>
                            Let Andrea take a look
                          </ClayCTA>
                        </div>
                      </div>
                      <motion.div
                        initial={reduce ? false : { opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.4 }}
                        className="lg:col-span-5 relative"
                      >
                        <div aria-hidden="true" className="absolute inset-0 translate-x-3 translate-y-3 rounded-[2rem] bg-claysoft" />
                        <img
                          src={SUMMARY_IMG}
                          alt="A parent and child sharing a quiet, warm moment together in morning light"
                          loading="lazy"
                          className="relative w-full aspect-[4/5] object-cover rounded-[2rem] shadow-[0_24px_50px_-25px_rgba(42,33,24,0.3)]"
                        />
                      </motion.div>
                    </div>
                  )}

                  {!intro && current === "andrea" && (
                    <div className="max-w-4xl grid grid-cols-1 sm:grid-cols-5 gap-10 items-center">
                      <div className="sm:col-span-2 relative">
                        <div aria-hidden="true" className="absolute inset-0 translate-x-3 translate-y-3 rounded-[2rem] bg-white/60" />
                        <img
                          src="/andrea-portrait.webp"
                          alt="Andrea, pediatric sleep consultant, seated at a wooden table with a coffee and an open notebook"
                          loading="lazy"
                          data-testid="conv-andrea-portrait"
                          className="relative w-full aspect-square object-cover object-[38%_25%] rounded-[2rem] shadow-[0_24px_50px_-25px_rgba(42,33,24,0.25)]"
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <p className="text-xs tracking-[0.25em] uppercase font-semibold text-clay">
                          The person on the other side of this
                        </p>
                        <p className="mt-4 font-serif font-light text-4xl sm:text-5xl text-ink">
                          Meet Andrea.
                        </p>
                        <p className="mt-3 font-serif italic font-light text-xl sm:text-2xl text-euca">
                          The person this conversation was always leading to.
                        </p>
                        <p className="mt-3 text-xs tracking-[0.25em] uppercase text-smoke">
                          Pediatric Sleep Consultant
                        </p>
                        <p className="mt-6 text-base sm:text-lg font-normal leading-relaxed text-smoke">
                          Andrea will receive what you just shared, so when you
                          connect, she already has the important context.
                        </p>
                        <div className="mt-8">
                          <ClayCTA testid="conv-andrea-continue" onClick={next}>
                            Continue
                          </ClayCTA>
                        </div>
                      </div>
                    </div>
                  )}

                  {!intro && current === "contact" && (
                    <div>
                      <Question label="Want Andrea to take a look?">Where should Andrea reach you?</Question>
                      <form onSubmit={submit} className="mt-10 space-y-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="conv-name" className="block text-xs tracking-[0.2em] uppercase font-semibold text-smoke mb-2">
                              First name
                            </label>
                            <input
                              id="conv-name"
                              data-testid="conv-name-input"
                              required
                              className={inputCls}
                              value={contact.first_name}
                              onChange={(e) => setContact((c) => ({ ...c, first_name: e.target.value }))}
                              placeholder="Your first name"
                              autoComplete="given-name"
                            />
                          </div>
                          <div>
                            <label htmlFor="conv-email" className="block text-xs tracking-[0.2em] uppercase font-semibold text-smoke mb-2">
                              Email
                            </label>
                            <input
                              id="conv-email"
                              type="email"
                              data-testid="conv-email-input"
                              required
                              className={inputCls}
                              value={contact.email}
                              onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                              placeholder="you@example.com"
                              autoComplete="email"
                              inputMode="email"
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor="conv-phone" className="block text-xs tracking-[0.2em] uppercase font-semibold text-smoke mb-2">
                            Phone (optional)
                          </label>
                          <input
                            id="conv-phone"
                            type="tel"
                            data-testid="conv-phone-input"
                            className={inputCls}
                            value={contact.phone}
                            onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                            placeholder="Best number"
                            autoComplete="tel"
                            inputMode="tel"
                          />
                        </div>
                        <div>
                          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-smoke mb-3">Preferred contact</p>
                          <div className="flex gap-3" role="radiogroup" aria-label="Preferred contact method">
                            {["Text", "Phone", "Email"].map((m) => (
                              <button
                                type="button"
                                key={m}
                                role="radio"
                                aria-checked={contact.preferred_contact === m}
                                data-testid={`conv-contact-${m.toLowerCase()}`}
                                onClick={() => setContact((c) => ({ ...c, preferred_contact: m }))}
                                className={`rounded-full px-6 py-3 text-sm font-medium border transition-all duration-300 ${
                                  contact.preferred_contact === m
                                    ? "bg-ink text-ivory border-ink"
                                    : "bg-white/70 border-ink/10 text-smoke hover:border-clay/60"
                                }`}
                              >
                                {m}
                              </button>
                            ))}
                          </div>
                        </div>

                        {failed && (
                          <p data-testid="conv-error" className="text-sm font-normal text-ink bg-claysoft rounded-xl px-5 py-4">
                            That didn&rsquo;t go through — nothing you wrote is
                            lost. Please try again.
                          </p>
                        )}

                        <div>
                          <ClayCTA type="submit" testid="conv-submit-button" disabled={sending}>
                            {sending ? "Sending, one moment" : failed ? "Retry — send my note to Andrea" : "Send my note to Andrea"}
                          </ClayCTA>
                          <p className="mt-5 text-xs font-normal text-smoke" data-testid="conv-privacy-note">
                            No spam. No selling your information. Just a way for
                            Andrea to follow up about what you shared.
                          </p>
                        </div>
                      </form>
                    </div>
                  )}

                  {!intro && current === "success" && (
                    <div data-testid="conv-success" className="max-w-xl mx-auto text-center">
                      <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
                        Sent
                      </p>
                      <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl leading-tight text-ink">
                        Okay. Andrea has it from here.
                      </h2>
                      <p className="mt-7 max-w-md mx-auto text-base sm:text-lg font-normal leading-relaxed text-smoke">
                        She has the important details you shared, so you
                        won&rsquo;t have to start the conversation from scratch.
                      </p>
                  <a
                    data-testid="conv-success-calendly"
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("book_assessment_click", { location: "conv_success" })}
                    className="mt-6 inline text-sm font-normal leading-relaxed text-smoke underline underline-offset-8 decoration-ink/20 hover:decoration-clay hover:text-ink transition-[text-decoration-color,color] duration-300"
                  >
                    Prefer to talk first? Book your complimentary pediatric sleep assessment
                  </a>
                      <p className="mt-4 max-w-md mx-auto text-base font-normal leading-relaxed text-smoke">
                        She&rsquo;ll follow up using your preferred contact
                        method to arrange your conversation.
                      </p>
                      <button
                        data-testid="conv-return-button"
                        onClick={handleClose}
                        className="mt-12 text-sm underline underline-offset-8 decoration-ink/25 hover:decoration-ink transition-[text-decoration-color] duration-300"
                      >
                        Return to the site
                      </button>
                    </div>
                  )}

                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
